import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { trackPDFDownload } from './analyticsEvents';

const generateDataMaturityPDF = (results, answers, sections) => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  let yPosition = 20;

  // Northstar Analytics Brand Colors
  const deepMidnightBlue = [0, 43, 69]; // #002B45 - Primary
  const tealBlue = [31, 181, 165]; // #1FB5A5 - Accent
  const polarisWhite = [249, 251, 255]; // #F9FBFF - Background
  const coolGraphite = [74, 74, 74]; // #4A4A4A - Text
  const arcticSky = [180, 225, 224]; // #B4E1E0 - Light Accent

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      doc?.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Helper function to get maturity level
  const getMaturityLevel = (score) => {
    if (score >= 4.5) return 'Advanced';
    if (score >= 3.5) return 'Proficient';
    if (score >= 2.5) return 'Developing';
    if (score >= 1.5) return 'Minimal';
    return 'Basic';
  };

  // Helper function to get color for score (using brand colors)
  const getScoreColor = (score) => {
    if (score >= 4.5) return tealBlue; // Advanced - Teal Blue
    if (score >= 3.5) return arcticSky; // Proficient - Arctic Sky
    if (score >= 2.5) return [245, 158, 11]; // Developing - Amber
    if (score >= 1.5) return [249, 115, 22]; // Minimal - Orange
    return [239, 68, 68]; // Basic - Red
  };

  // Calculate overall score
  const overallScore = Object.values(results?.sectionScores)?.reduce((sum, score) => sum + score, 0) / Object.keys(results?.sectionScores)?.length;
  const maturityLevel = getMaturityLevel(overallScore);

  // HEADER - Company branding without logo
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.rect(0, 0, pageWidth, 50, 'F');
  
  // Add gradient effect with Teal Blue strip
  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.rect(0, 45, pageWidth, 5, 'F');
  
  // Company name and report title (logo removed)
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(24);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Northstar Analytics', pageWidth / 2, 22, { align: 'center' });
  doc?.setFontSize(12);
  doc?.setFont('helvetica', 'normal');
  doc?.text('Data Maturity Assessment Report', pageWidth / 2, 33, { align: 'center' });
  doc?.setFontSize(9);
  doc?.setTextColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  doc?.text(`Generated: ${new Date()?.toLocaleDateString()}`, pageWidth / 2, 42, { align: 'center' });

  yPosition = 65;

  // EXECUTIVE SUMMARY
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(18);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Executive Summary', 20, yPosition);
  
  // Decorative line with Teal Blue
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  doc?.line(20, yPosition + 2, 60, yPosition + 2);
  yPosition += 12;

  // Overall Score Box with brand styling
  const scoreColor = getScoreColor(overallScore);
  
  // Outer border with Deep Midnight Blue
  doc?.setDrawColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setLineWidth(1);
  doc?.roundedRect(20, yPosition, 170, 35, 3, 3, 'S');
  
  // Inner fill with score color
  doc?.setFillColor(scoreColor?.[0], scoreColor?.[1], scoreColor?.[2]);
  doc?.roundedRect(21, yPosition + 1, 168, 33, 3, 3, 'F');
  
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text(`Overall Maturity Score: ${overallScore?.toFixed(1)}/5.0`, 25, yPosition + 14);
  doc?.setFontSize(14);
  doc?.text(`Maturity Level: ${maturityLevel}`, 25, yPosition + 26);
  yPosition += 45;

  // Interpretation
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  const interpretationLines = doc?.splitTextToSize(results?.interpretation, pageWidth - 40);
  doc?.text(interpretationLines, 20, yPosition);
  yPosition += interpretationLines?.length * 6 + 10;

  // ROI Impact with Teal Blue accent
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.text(`Potential ROI Impact: ${results?.roiImpact}`, 20, yPosition);
  yPosition += 15;

  checkPageBreak(40);

  // SECTION SCORES BREAKDOWN
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Detailed Assessment by Category', 20, yPosition);
  
  // Decorative line with Teal Blue
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  doc?.line(20, yPosition + 2, 90, yPosition + 2);
  yPosition += 12;

  const sectionDetails = [
    { key: 'dataInfrastructure', name: 'Data Infrastructure' },
    { key: 'analyticsCapabilities', name: 'Analytics Capabilities' },
    { key: 'governanceMaturity', name: 'Governance Maturity' },
    { key: 'teamSkills', name: 'Team Skills' },
    { key: 'strategicAlignment', name: 'Strategic Alignment' }
  ];

  // Create table data for section scores
  const sectionTableData = sectionDetails?.map(section => {
    const score = results?.sectionScores?.[section?.key] || 0;
    const level = getMaturityLevel(score);
    return [section?.name, score?.toFixed(1), level];
  });

  doc?.autoTable({
    startY: yPosition,
    head: [['Category', 'Score', 'Maturity Level']],
    body: sectionTableData,
    theme: 'grid',
    headStyles: { 
      fillColor: deepMidnightBlue, 
      fontSize: 11, 
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 10, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 40, halign: 'center' },
      2: { cellWidth: 60, halign: 'center' }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  checkPageBreak(40);

  // INDUSTRY BENCHMARKS
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.text('Industry Benchmarks Comparison', 20, yPosition);
  
  // Decorative line with Teal Blue
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  doc?.line(20, yPosition + 2, 85, yPosition + 2);
  yPosition += 12;

  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.text('Your organization compared to industry standards:', 20, yPosition);
  yPosition += 8;

  // Benchmark data
  const benchmarkData = [
    ['Industry Average', '3.2', 'Developing'],
    ['Top Performers (75th percentile)', '4.1', 'Proficient'],
    ['Leaders (90th percentile)', '4.6', 'Advanced'],
    ['Your Organization', overallScore?.toFixed(1), maturityLevel]
  ];

  doc?.autoTable({
    startY: yPosition,
    head: [['Benchmark', 'Score', 'Level']],
    body: benchmarkData,
    theme: 'striped',
    headStyles: { 
      fillColor: deepMidnightBlue, 
      fontSize: 11, 
      fontStyle: 'bold',
      textColor: [255, 255, 255]
    },
    bodyStyles: { fontSize: 10, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 80 },
      1: { cellWidth: 40, halign: 'center' },
      2: { cellWidth: 60, halign: 'center' }
    },
    didParseCell: function(data) {
      if (data?.row?.index === benchmarkData?.length - 1 && data?.section === 'body') {
        data.cell.styles.fillColor = tealBlue;
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
      }
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 20, right: 20 }
  });

  yPosition = doc?.lastAutoTable?.finalY + 15;
  checkPageBreak(40);

  // KEY RECOMMENDATIONS
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.text('Personalized Recommendations', 20, yPosition);
  
  // Decorative line with Teal Blue
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  doc?.line(20, yPosition + 2, 80, yPosition + 2);
  yPosition += 12;

  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
  doc?.text('Based on your assessment, we recommend the following strategic priorities:', 20, yPosition);
  yPosition += 10;

  results?.recommendations?.forEach((recommendation, index) => {
    checkPageBreak(20);
    // Teal Blue bullet point
    doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.circle(23, yPosition - 2, 2, 'F');
    const recLines = doc?.splitTextToSize(recommendation, pageWidth - 50);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.text(recLines, 30, yPosition);
    yPosition += recLines?.length * 6 + 5;
  });

  yPosition += 10;
  checkPageBreak(60);

  // DETAILED SECTION ANALYSIS
  doc?.addPage();
  yPosition = 20;
  doc?.setFontSize(18);
  doc?.setFont('helvetica', 'bold');
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.text('Detailed Section Analysis', 20, yPosition);
  
  // Decorative line with Teal Blue
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(2);
  doc?.line(20, yPosition + 2, 75, yPosition + 2);
  yPosition += 15;

  sections?.forEach((section, sectionIndex) => {
    checkPageBreak(50);
    
    const sectionScore = results?.sectionScores?.[section?.id] || 0;
    const sectionLevel = getMaturityLevel(sectionScore);
    const sectionColor = getScoreColor(sectionScore);

    // Section header with brand styling
    doc?.setDrawColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.setLineWidth(0.5);
    doc?.roundedRect(20, yPosition, 170, 14, 2, 2, 'S');
    doc?.setFillColor(sectionColor?.[0], sectionColor?.[1], sectionColor?.[2]);
    doc?.roundedRect(20.5, yPosition + 0.5, 169, 13, 2, 2, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(12);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${section?.title}: ${sectionScore?.toFixed(1)}/5.0 (${sectionLevel})`, 25, yPosition + 9);
    yPosition += 20;

    // Section description
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'italic');
    const descLines = doc?.splitTextToSize(section?.description, pageWidth - 40);
    doc?.text(descLines, 20, yPosition);
    yPosition += descLines?.length * 5 + 8;

    // Question responses
    doc?.setFont('helvetica', 'normal');
    section?.questions?.forEach((question, qIndex) => {
      checkPageBreak(25);
      
      const answer = answers?.[question?.id] || 0;
      const answerLabel = ['', 'Not at all', 'Minimal', 'Developing', 'Proficient', 'Advanced']?.[answer];
      
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
      doc?.text(`Q${qIndex + 1}: ${question?.title}`, 25, yPosition);
      yPosition += 6;
      
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(8);
      doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
      const qLines = doc?.splitTextToSize(question?.description, pageWidth - 50);
      doc?.text(qLines, 25, yPosition);
      yPosition += qLines?.length * 4 + 3;
      
      // Answer with Teal Blue accent color
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'bold');
      doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
      doc?.text(`Response: ${answerLabel} (${answer}/5)`, 25, yPosition);
      yPosition += 8;
    });

    yPosition += 5;
  });

  // FOOTER - Add to all pages
  const pageCount = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc?.setPage(i);
    
    // Footer background with Deep Midnight Blue
    doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
    doc?.rect(0, pageHeight - 15, pageWidth, 15, 'F');
    
    // Footer text
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'normal');
    doc?.text('Northstar Analytics | Data Maturity Assessment', 20, pageHeight - 7);
    doc?.text(`Page ${i} of ${pageCount}`, pageWidth - 30, pageHeight - 7);
    
    // Teal Blue accent line
    doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
    doc?.setLineWidth(1);
    doc?.line(0, pageHeight - 15, pageWidth, pageHeight - 15);
  }

  // Save the PDF
  doc?.save(`Northstar-Data-Maturity-Report-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);

  // Track PDF download
  trackPDFDownload('Data Maturity Assessment', `Northstar-Data-Maturity-Report-${new Date()?.toISOString()?.split('T')?.[0]}`);
};

export default generateDataMaturityPDF;
